import {
  GRANTABLE_PERMISSIONS,
  GRANTABLE_PERMISSION_NAMES,
  compareRoles,
} from "@colossus/contracts";
import type { GrantablePermission, RoleAnchor } from "@colossus/contracts";
import type { ApiClient } from "@colossus/api-client";
import type { RoleEntity } from "./entities.ts";

export interface RoleFacts {
  readonly roleId: string;
  readonly name: string;
  readonly holders: number;
  readonly color: string | null;
  readonly position: number | null;
  readonly permissions: string | null;
  readonly levels: { readonly min: number; readonly p10: number; readonly median: number } | null;
  readonly imported: { readonly min: number; readonly median: number } | null;
  readonly withheld: boolean;
  readonly icon: string | null;
  readonly unicodeEmoji: string | null;
}

type RoleStats = Pick<RoleFacts, "roleId" | "holders" | "levels" | "imported" | "withheld">;

const STATS_BATCH = 100;

export const roleStatsIn = async (
  client: Pick<ApiClient, "call">,
  guildId: string,
  ids: readonly string[],
): Promise<{ readonly rows: readonly RoleStats[]; readonly complete: boolean }> => {
  const batches = Array.from({ length: Math.ceil(ids.length / STATS_BATCH) }, (_unused, at) =>
    ids.slice(at * STATS_BATCH, (at + 1) * STATS_BATCH).join(","),
  );
  const answers = await Promise.all(
    batches.map((roleIds) =>
      client
        .call("get", "/t/{tenantId}/api/leveling/roles/stats", { query: { guildId, roleIds } })
        .then(
          (result) => (result.ok ? result.data : null),
          () => null,
        ),
    ),
  );
  return { rows: answers.flatMap((rows) => rows ?? []), complete: !answers.includes(null) };
};

export const roleFactsOf = (
  roles: readonly RoleEntity[],
  stats: readonly RoleStats[],
): RoleFacts[] => {
  const known = new Map(roles.map((role) => [role.id, role]));
  return stats.map((row) => {
    const role = known.get(row.roleId);
    return {
      roleId: row.roleId,
      name: role === undefined ? row.roleId : role.name,
      holders: row.holders,
      color: role === undefined ? null : role.color,
      position: role === undefined ? null : role.position,
      permissions: role === undefined ? null : role.permissions,
      levels: row.levels,
      imported: row.imported,
      withheld: row.withheld,
      icon: role === undefined ? null : role.icon,
      unicodeEmoji: role === undefined ? null : role.unicodeEmoji,
    };
  });
};

export type LevelSource = "name" | "import" | "members" | "withheld";

interface LevelGuess {
  readonly level: number | null;
  readonly source: LevelSource | null;
}

const LEVEL_WORD = "level|lvl|lv|rank|tier|уровень|ур|ранг";

const LEVEL_BEFORE = new RegExp(
  `(?<![\\p{L}\\p{N}])(?:${LEVEL_WORD})\\.?\\s*#?\\s*(\\d{1,4})(?!\\p{N})`,
  "iu",
);

const LEVEL_AFTER = new RegExp(`(?<!\\p{N})(\\d{1,4})\\s*(?:${LEVEL_WORD})(?![\\p{L}])`, "iu");

const MAX_GUESSED_LEVEL = 1000;

const asLevel = (digits: string | undefined): number | null => {
  if (digits === undefined) return null;
  const level = Number(digits);
  return level >= 1 && level <= MAX_GUESSED_LEVEL ? level : null;
};

const levelFromName = (name: string): number | null =>
  asLevel((LEVEL_BEFORE.exec(name) ?? LEVEL_AFTER.exec(name))?.[1]);

const numberInName = (name: string): number | null => {
  const numbers = name.match(/\d+/gu);
  return numbers === null || numbers.length !== 1 ? null : asLevel(numbers[0]);
};

const DIVIDER = /^[^\p{L}\p{N}\s]{2,}.*[^\p{L}\p{N}\s]{2,}$/u;

export const isDivider = (name: string): boolean => DIVIDER.test(name.trim());

const SECTION_NAME: Readonly<Record<RoleAnchor, RegExp>> = {
  perks:
    /(?<![\p{L}])(?:levels?|lvls?|ranks?|tiers?|xp|achievements?|milestones?|badges?|awards?|perks?|supporters?|boosters?|donors?|patrons?|vips?|уровн|ранг|достижени|наград|значк|привилеги|спонсор|бустер|донат)/iu,
  personal: /(?<![\p{L}])(?:personal|custom|own|личн|кастом|собствен)/iu,
};

type Ranked = Pick<RoleFacts, "roleId" | "name" | "position">;

const inDiscordOrder = <R extends Ranked>(roles: readonly R[]): readonly R[] =>
  roles.toSorted((left, right) =>
    compareRoles(
      { id: left.roleId, position: left.position },
      { id: right.roleId, position: right.position },
    ),
  );

export const rolesUnder = (
  roles: readonly RoleFacts[],
  dividerId: string,
): readonly RoleFacts[] => {
  const ordered = inDiscordOrder(roles);
  const from = ordered.findIndex((role) => role.roleId === dividerId);
  if (from < 0) return [];
  const below = ordered.slice(from + 1);
  const next = below.findIndex((role) => isDivider(role.name));
  return next < 0 ? below : below.slice(0, next);
};

const longestLadder = <R extends Ranked>(roles: readonly R[]): readonly R[] => {
  const numbered = inDiscordOrder(roles).flatMap((role) => {
    const level = numberInName(role.name);
    return level === null || isDivider(role.name) ? [] : [{ role, level }];
  });
  const runs: (readonly R[])[] = [];
  numbered.forEach((entry, at) => {
    const best = numbered
      .slice(0, at)
      .map((before, index) => ({ before, run: runs[index] ?? [] }))
      .filter(({ before }) => before.level > entry.level)
      .reduce<readonly R[]>(
        (longest, { run }) => (run.length > longest.length ? run : longest),
        [],
      );
    runs.push([...best, entry.role]);
  });
  return runs.reduce<readonly R[]>(
    (longest, run) => (run.length > longest.length ? run : longest),
    [],
  );
};

const MIN_LADDER = 3;

export const levelsFromNames = (
  roles: readonly RoleFacts[],
  category: string | null,
): ReadonlyMap<string, number> => {
  const levels = new Map<string, number>();
  for (const role of roles) {
    const stated = isDivider(role.name) ? null : levelFromName(role.name);
    if (stated !== null) levels.set(role.roleId, stated);
  }
  const ladder = category === null ? longestLadder(roles) : [];
  const counted =
    category === null ? (ladder.length >= MIN_LADDER ? ladder : []) : rolesUnder(roles, category);
  for (const role of counted) {
    const number = numberInName(role.name);
    if (number !== null && !levels.has(role.roleId)) levels.set(role.roleId, number);
  }
  return levels;
};

export const guessAnchor = (
  roles: readonly Ranked[],
  anchor: RoleAnchor,
  held: readonly string[],
): string | null => {
  const ordered = inDiscordOrder(roles);
  const titled = ordered.find(
    (role) => isDivider(role.name) && SECTION_NAME[anchor].test(role.name),
  );
  if (titled !== undefined) return titled.roleId;
  const own = ordered.filter((role) => held.includes(role.roleId) && !isDivider(role.name));
  const ladder = own.length === 0 && anchor === "perks" ? longestLadder(roles) : own;
  const top = ladder[0];
  if (top === undefined || (own.length === 0 && ladder.length < MIN_LADDER)) return null;
  const above = ordered.slice(0, ordered.indexOf(top)).findLast((role) => isDivider(role.name));
  return above === undefined ? null : above.roleId;
};

export const guessLevel = (role: RoleFacts, named: ReadonlyMap<string, number>): LevelGuess => {
  const fromName = named.get(role.roleId);
  if (fromName !== undefined) return { level: fromName, source: "name" };
  if (role.withheld) return { level: null, source: "withheld" };
  if (role.imported !== null && role.imported.min >= 1) {
    return { level: role.imported.min, source: "import" };
  }
  if (role.levels !== null && role.levels.p10 >= 1) {
    return { level: role.levels.p10, source: "members" };
  }
  return { level: null, source: null };
};

const bitsOf = (permissions: string | null): bigint =>
  permissions === null ? 0n : BigInt(permissions);

const namesIn = (bits: bigint): GrantablePermission[] =>
  GRANTABLE_PERMISSION_NAMES.filter((name) => (bits & GRANTABLE_PERMISSIONS[name]) !== 0n);

export const perksOf = (
  role: RoleFacts,
  everyone: RoleFacts | undefined,
): readonly GrantablePermission[] =>
  namesIn(bitsOf(role.permissions) & ~(everyone === undefined ? 0n : bitsOf(everyone.permissions)));

export const heldPerksOf = (role: {
  readonly permissions: string | null;
}): readonly GrantablePermission[] => namesIn(bitsOf(role.permissions));

export const offeredRoles = (
  roles: readonly RoleFacts[],
  excluded: ReadonlySet<string>,
  named: ReadonlyMap<string, number>,
  { dividers = false }: { readonly dividers?: boolean } = {},
): readonly RoleFacts[] =>
  inDiscordOrder(
    roles.filter((role) => !excluded.has(role.roleId) && isDivider(role.name) === dividers),
  ).toSorted((left, right) => Number(named.has(right.roleId)) - Number(named.has(left.roleId)));
